import {
  createContext,
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
} from "react";
import { Outlet, useLocation, useParams } from "react-router";
import { useSearchParams } from "react-router-dom";
import {
  BackgroundJobType,
  useBackgroundJobs,
} from "context/BackgroundJobsContext";
import { useProject } from "context/ProjectContext";
import { useUser } from "context/UserContext";
import useGraphQLQuery from "hooks/api/GQL";
import queryConfig from "hooks/api/GQL/queryConfig";
import usePermissions from "hooks/permissions";
import useIsFeatureFlagOn from "hooks/useIsFeatureFlagOn";
import {
  IStructuredDocument,
  IStructuredDocumentResponseDataGraphQL,
  TStructuredDocumentParams,
} from "models/assets.models";
import { EventType } from "models/tracking.models";
import { TStructuredDocumentTreeItem } from "screens/Project/screens/StructuredDocument/StructuredDocument.types";
import { services } from "services";
import { QueryAPIKey } from "utils/constants/api.constants";
import { AssetType } from "utils/constants/assets.constants";
import { FeatureFlagName } from "utils/constants/featureFlags.constants";
import { getIsAssetCreatedByUser } from "utils/helpers/asset/getIsAssetCreatedByUser";
import useReorderStructuredDocument from "../../screens/Project/hooks/documents/structuredDocuments/useReorderStructuredDocument";
import { StructuredDocumentAction } from "../../screens/Project/screens/StructuredDocument/StructuredDocument.constants";
import { getStructuredDocumentActions } from "../../screens/Project/screens/StructuredDocument/StructuredDocument.utils";

export type TStructuredDocumentContextValue = {
  document: IStructuredDocument | undefined;
  isDocumentBusy: boolean;
  structuredDocumentActions: Set<StructuredDocumentAction>;
  isSortingInProgress: boolean;
  setIsSortingInProgress: (isSorting: boolean) => void;
  disableSorting: boolean;
  treeViewItems: TStructuredDocumentTreeItem[];
  handleTreeViewItemsChanged: (items: TStructuredDocumentTreeItem[]) => void;
  expandedSectionPaths: Set<string>;
  setExpandedSectionPaths: Dispatch<SetStateAction<Set<string>>>;
  isErrorLoadingDocument: boolean;
  canManageDocument: boolean;
  isPermissionsLoading: boolean;
};

export const StructuredDocumentContext = createContext<
  TStructuredDocumentContextValue | undefined
>(undefined);

const StructuredDocumentProvider = () => {
  const { project, isProjectLoading } = useProject();
  const { user } = useUser();
  const { structuredDocumentId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const isSimplifyContentEnabled = useIsFeatureFlagOn(
    FeatureFlagName.EnableSimplifyContent,
  );

  const { permissions, isLoading: isPermissionsLoading } = usePermissions(
    project?.id ?? "",
  );

  // On the Management view, we should not have access to previous versions as a search parameter, so the user cannot change it manually.
  const version = searchParams.get("version") || location.state?.version || "";

  const { getJob } = useBackgroundJobs();

  const statusChangeJob = getJob(
    structuredDocumentId,
    BackgroundJobType.StatusChange,
  );

  const {
    data: structuredDocument,
    isFetching,
    isError,
  } = useGraphQLQuery<
    IStructuredDocumentResponseDataGraphQL,
    TStructuredDocumentParams,
    IStructuredDocument
  >({
    ...queryConfig.getStructuredDocument,
    queryKey: [QueryAPIKey.GetStructuredDocument],
    variables: {
      assetId: structuredDocumentId!,
      version: version ?? "",
      includeAssetSubmissionStatus: isSimplifyContentEnabled,
    },
    queryOptions: {
      ...queryConfig.getStructuredDocument.queryOptions,
      enabled: !!structuredDocumentId,
    },
  });

  const {
    isSortingInProgress,
    setIsSortingInProgress,
    sortStatus,
    treeViewItems,
    handleTreeViewItemsChanged,
    expandedSectionPaths,
    setExpandedSectionPaths,
  } = useReorderStructuredDocument({ structuredDocument });

  useEffect(() => {
    if (structuredDocument && project?.id) {
      services.tracking.addTrackingEvent({
        assetId: structuredDocument.id,
        assetType: AssetType.Document,
        eventType: EventType.AppContentView,
        projectId: project.id,
      });
    }
  }, [structuredDocument, project?.id]);

  const isEditStructuredDocumentSectionTitleEnabled = useIsFeatureFlagOn(
    FeatureFlagName.EnableEditStructureDocumentSectionTitle,
  );

  const structuredDocumentActions = useMemo(
    () =>
      getStructuredDocumentActions({
        document: structuredDocument,
        user,
        permissions: permissions,
        isProjectActive: project?.active ?? false,
        isEditStructuredDocumentSectionTitleEnabled,
      }),
    [
      structuredDocument,
      user,
      permissions,
      project?.active,
      isEditStructuredDocumentSectionTitleEnabled,
    ],
  );

  const canManageDocument =
    !!project?.active &&
    permissions.canReorderStructuredDocument &&
    (permissions.canManageStructuredDocument ||
      getIsAssetCreatedByUser(structuredDocument, user));

  const isDocumentBusy = !!statusChangeJob || isFetching || isSortingInProgress;

  const value = useMemo(
    () => ({
      document: structuredDocument,
      isDocumentBusy,
      structuredDocumentActions,
      isSortingInProgress,
      setIsSortingInProgress,
      disableSorting:
        isDocumentBusy && (!sortStatus || sortStatus === "saving"),
      treeViewItems,
      handleTreeViewItemsChanged,
      expandedSectionPaths,
      setExpandedSectionPaths,
      isErrorLoadingDocument: isError,
      canManageDocument,
      // permissions don't start loading until the project is done loading
      isPermissionsLoading: isProjectLoading || isPermissionsLoading,
    }),
    [
      structuredDocument,
      isDocumentBusy,
      structuredDocumentActions,
      isSortingInProgress,
      setIsSortingInProgress,
      sortStatus,
      treeViewItems,
      handleTreeViewItemsChanged,
      expandedSectionPaths,
      setExpandedSectionPaths,
      isError,
      canManageDocument,
      isProjectLoading,
      isPermissionsLoading,
    ],
  );

  return (
    <StructuredDocumentContext.Provider value={value}>
      <Outlet />
    </StructuredDocumentContext.Provider>
  );
};

export default StructuredDocumentProvider;
